// @ts-strict-ignore
import * as React from 'react';

import {ButtonTheme} from 'common/components/button/constants';
import {SelectTheme} from 'common/components/select/constants';
import {SelectReact} from 'common/components/select';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import {Directions} from 'common/components/drop-menu/types';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {CountUsersOnceState} from 'common/report/constants';
import {MATH_TYPE} from 'common/types/reports/bookmark';
import {MenuChangeEvent} from 'common/components/select/types';
import {InsightsCountType} from 'common/query-builder/query-entry-section/types';
import {MenuType} from 'common/query-builder/query-block/types';
import {SelectorType} from 'common/query-builder/query-block/constants';
import {useTranslation} from 'common/i18n/hooks';
import {useQueryBuilderContext} from 'common/contexts/react/query-builder-context';
import {SvgIconType} from 'common/components/svg-icon/types';
import {Segmentation} from 'common/types/reports/query';

import {propertyItemToSegmentation} from '../properties-menu/util';
import {
  IncludeCustomPropertyType,
  isProperty,
  MenuItemType,
  SelectedPropertyItem,
  Selection,
} from '../properties-menu/types';
import {segmentationToPropertySelection} from '../segment-menu/util';
/*eslint-disable-next-line import/no-cycle*/
import {QueryBlockReact} from '../index';
import {PAGE_DURATION} from '../properties-menu/constants';
import {PropertySectionItem} from '../properties-menu/item-menu-types';
import type {CountMenuProps, CountMenuState} from './index';
import {
  AggregationSelector,
  COUNT_USERS_ONCE_OPTIONS,
  CountTypeSelector,
  DefaultCountUsersOnceType,
  SubSelectionTypes,
} from './constants';
import {
  generateCountUsersOnceMenuOptions,
  getAggregationIcon,
  getAggregationsSectionForCountType,
  getNestedPropertyIcon,
  getPropertyIcon,
  getSelectedAggregationLabel,
  getSubSelectionDescription,
} from './util';

const CountUsersOnceSelection = (props: {
  isOpen: boolean;
  countTypeSelection: SectionItem<CountTypeSelector>;
  selectionType: SubSelectionTypes;
  selectionIndex: number;
  onMenuChange: (ev: MenuChangeEvent) => void;
  countMenuProps: CountMenuProps;
  countMenuState: CountMenuState;
  timePeriodLabel: string;
  disableTooltips: boolean;
  onSelectAggregation: (aggregation, subSelectionIndex) => void;
}) => {
  const {t, print} = useTranslation();

  const countUsersOnceType =
    props.countMenuProps.selected.countUsersOnceType || DefaultCountUsersOnceType.get(props.countMenuProps.metricType);
  const countUsersOnceState = props.countMenuProps.countUsersOnceState;
  const sections = generateCountUsersOnceMenuOptions({
    selectedCountUsersOnceType: countUsersOnceType,
    timePeriodLabel: props.timePeriodLabel,
    countUsersOnceState,
    metricType: props.countMenuProps.metricType,
    dataGroupId: props.countMenuProps.dataGroupId,
  });

  const isCountUsersOnceDisabled = countUsersOnceState === CountUsersOnceState.Disabled;
  const selectTooltipText = isCountUsersOnceDisabled
    ? t(`query-builder.count-menu.count-users-once.breakdown-required`)
    : COUNT_USERS_ONCE_OPTIONS[countUsersOnceType].description({
        timePeriod: props.timePeriodLabel,
      });
  const disableTooltip = !isCountUsersOnceDisabled && props.disableTooltips;

  return (
    <SelectReact
      open={props.isOpen && !isCountUsersOnceDisabled}
      elref={`countUsersOnceSelect`}
      label={print(COUNT_USERS_ONCE_OPTIONS[countUsersOnceType].selectLabel())}
      disableSearch
      disabled={isCountUsersOnceDisabled}
      trackName={props.countMenuProps.trackName}
      menuWidth={null}
      menuHeight={0}
      icon={{leftIcon: getAggregationIcon(props.countTypeSelection, props.selectionType), rightIcon: `double-caret`}}
      alwaysShowCaret
      theme={SelectTheme.Secondary}
      disableTooltip={disableTooltip}
      tooltip={
        !disableTooltip && {
          text: selectTooltipText,
          size: DisplaySize.Small,
          placement: Placement.Right,
        }
      }
      menuAlignment={Directions.RightCenter}
      onMenuChange={props.onMenuChange}
      onChange={({detail: {item: aggregation}}) => {
        props.onSelectAggregation(aggregation, props.selectionIndex);
      }}
      sections={sections}
    />
  );
};

const AggregationSelection = (props: {
  isOpen: boolean;
  countTypeSelection: SectionItem<CountTypeSelector>;
  selectionType: SubSelectionTypes;
  selectionIndex: number;
  operator: MATH_TYPE;
  sections: Array<Section<AggregationSelector>>;
  onMenuChange: (ev: MenuChangeEvent) => void;
  countMenuProps: CountMenuProps;
  countMenuState: CountMenuState;
  disableTooltips: boolean;
  onSelectAggregation: (aggregation, subSelectionIndex) => void;
}) => {
  const {print} = useTranslation();
  const {selectedAggregationOperator, selectedAggregationOperatorPerUser} = props.countMenuState;

  return (
    <SelectReact
      open={props.isOpen}
      elref={`aggregationSelect`}
      label={print(
        getSelectedAggregationLabel({
          metricType: props.countMenuProps.metricType,
          operator: props.operator,
          countTypeSelection: props.countTypeSelection,
          percentile: props.countMenuProps.selected?.percentile,
        }),
      )}
      disableSearch
      trackName={props.countMenuProps.trackName}
      menuWidth={180}
      menuHeight={0}
      icon={{leftIcon: getAggregationIcon(props.countTypeSelection, props.selectionType), rightIcon: `double-caret`}}
      alwaysShowCaret
      theme={SelectTheme.Secondary}
      {...(!props.disableTooltips && {
        tooltip: {
          text: getSubSelectionDescription({
            searchFirstLevelAggregation: props.selectionType === SubSelectionTypes.Aggregation,
            countTypeSelection: props.countTypeSelection,
            aggregationOperator: selectedAggregationOperator,
            aggregationOperatorPerUser: selectedAggregationOperatorPerUser,
            dataGroupId: props.countMenuProps.dataGroupId,
            dataGroups: props.countMenuProps.dataGroups,
          }),
          size: DisplaySize.Small,
          placement: Placement.Right,
        },
      })}
      menuAlignment={Directions.RightCenter}
      onMenuChange={props.onMenuChange}
      onChange={({detail: {item: aggregation}}) => {
        props.onSelectAggregation(aggregation, props.selectionIndex);
      }}
      sections={props.sections}
    />
  );
};

const useIsRightAlignedQb = (): boolean => {
  const queryBuilderContext = useQueryBuilderContext();
  return Boolean(queryBuilderContext.isCoreReportQb && queryBuilderContext.qbAlignment === Directions.Right);
};

const PropertySelection = (props: {
  countMenuProps: CountMenuProps;
  contextPanelParent: HTMLElement;
  onSelect: (selection: SelectedPropertyItem) => void;
  // slightly different from list & revenue & nested property selection
  selected: PropertySectionItem | Selection;
  eventNames: string[];
  label: string;
  allDataGroups: boolean;
  selectorIcon: SvgIconType;
  otherAggregationOptions: Array<PropertySectionItem<string>>;
  includeCustomProperties: IncludeCustomPropertyType;
  open: boolean;
  listProperty: Segmentation;
  menuItemTypes: MenuItemType[];
}) => {
  const isRightAlignedCoreQb = useIsRightAlignedQb();

  return (
    <QueryBlockReact
      // configurable props
      selectorIcon={props.selectorIcon}
      label={props.label}
      open={props.open}
      selected={props.selected}
      otherAggregationOptions={props.otherAggregationOptions}
      eventNames={props.eventNames}
      includeCustomProperties={props.includeCustomProperties}
      allDataGroups={props.allDataGroups}
      listProperty={props.listProperty}
      menuItemTypes={props.menuItemTypes}
      // shared props
      type={MenuType.Properties}
      readonly={props.countMenuProps.readonly}
      enableWorkspaceFilterRestrictions={props.countMenuProps.enableWorkspaceFilterRestrictions}
      workspaceId={props.countMenuProps.workspaceId}
      hideEvents={props.countMenuProps.hideEvents}
      projectHasPeople={props.countMenuProps.projectHasPeople}
      dataGroupId={props.countMenuProps.dataGroupId}
      projectId={props.countMenuProps.projectId}
      userId={props.countMenuProps.userId}
      showBorrowedProperties={props.countMenuProps.showBorrowedProperties}
      hideEditControls={props.countMenuProps.hideEditControls}
      selectorRightIcon='double-caret'
      selectorType={SelectorType.Button}
      showSpecialDatePropertyUnit
      theme={ButtonTheme.Secondary}
      trackName={props.countMenuProps.trackName}
      menuAlignment={Directions.RightCenter}
      contextPanelAlignment={isRightAlignedCoreQb ? Directions.Left : Directions.Right}
      menuOffset={4}
      contextPanelParentElement={props.contextPanelParent}
      onSelect={props.onSelect}
    />
  );
};

export const SubSelections = (props: {
  selections: SubSelectionTypes[];
  countTypeIndex: number;
  countMenuProps: CountMenuProps;
  countMenuState: CountMenuState;
  timePeriodLabel: string;
  disableTooltips: boolean;
  canExpandCurrentSubSelection: (index: number, type: SubSelectionTypes) => boolean;
  onMenuChange: (menuState: Partial<CountMenuState>) => void;
  onSelectProperty: (options: {
    selection: SelectedPropertyItem;
    selectionIndex: number;
    isNestedProperty: boolean;
    isRevenueProperty: boolean;
  }) => void;
  onSelectAggregation: (aggregation: SectionItem<AggregationSelector>, subSelectionIndex: number) => void;
  contextPanelParent: HTMLElement;
}) => {
  const {countTypeSelections, selectedAggregationOperator, selectedAggregationOperatorPerUser} = props.countMenuState;
  const countTypeSelection = props.countMenuState.countTypeSelections[props.countTypeIndex];

  return (
    <>
      {props.selections.map((selection, idx) => {
        const previousSelectionType = props.selections[idx - 1];
        const expandCurrentSubSelection = props.canExpandCurrentSubSelection(
          props.countTypeIndex,
          previousSelectionType,
        );
        if (!expandCurrentSubSelection) return null;

        const key = `${JSON.stringify(countTypeSelection)}-${idx}-${selection}`;

        switch (selection) {
          case SubSelectionTypes.Property: {
            let otherAggregationOptions = props.countMenuProps.otherAggregationOptions;
            // Temporarily filter out duration from Aggregate Property per User till it's supported on the backend
            if (countTypeSelection.value.countType === InsightsCountType.PropertyValuePerUser) {
              otherAggregationOptions = otherAggregationOptions.filter((opt) => opt.value !== PAGE_DURATION.value);
            }
            return (
              <PropertySelection
                open={props.countMenuState.isPropertyMenuOpen}
                selected={props.countMenuState.selectedProperty}
                onSelect={(selectedProperty) =>
                  props.onSelectProperty({
                    selection: selectedProperty,
                    selectionIndex: idx,
                    isNestedProperty: false,
                    isRevenueProperty: false,
                  })
                }
                label='Select Property'
                selectorIcon={getPropertyIcon(props.countMenuState.selectedProperty)}
                otherAggregationOptions={otherAggregationOptions}
                eventNames={props.countMenuProps.eventNames}
                allDataGroups={props.countMenuProps.allDataGroups}
                includeCustomProperties={props.countMenuProps.includeCustomProperties}
                listProperty={null}
                menuItemTypes={null}
                // shared
                key={key}
                countMenuProps={props.countMenuProps}
                contextPanelParent={props.contextPanelParent}
              />
            );
          }
          case SubSelectionTypes.RevenueProperty:
            return (
              isProperty(props.countMenuState.selectedProperty) && (
                <PropertySelection
                  open={props.countMenuState.isRevenuePropertyMenuOpen}
                  selected={props.countMenuState.selectedProperty.behavior?.property}
                  onSelect={(selectedProperty) =>
                    props.onSelectProperty({
                      selection: selectedProperty,
                      selectionIndex: idx,
                      isNestedProperty: false,
                      isRevenueProperty: true,
                    })
                  }
                  label='Select Revenue Property'
                  selectorIcon={getPropertyIcon(props.countMenuState.selectedProperty.behavior?.property)}
                  otherAggregationOptions={null}
                  eventNames={[]}
                  allDataGroups={props.countMenuProps.allDataGroups}
                  includeCustomProperties={IncludeCustomPropertyType.None}
                  listProperty={null}
                  menuItemTypes={[MenuItemType.SCDProperty]}
                  // shared
                  key={key}
                  countMenuProps={props.countMenuProps}
                  contextPanelParent={props.contextPanelParent}
                />
              )
            );
          case SubSelectionTypes.NestedProperty:
            return (
              isProperty(props.countMenuState.selectedProperty) && (
                <PropertySelection
                  open={props.countMenuState.isNestedPropertyMenuOpen}
                  selected={segmentationToPropertySelection(props.countMenuState.selectedProperty.listItemGroup)}
                  onSelect={(selectedProperty) =>
                    props.onSelectProperty({
                      selection: selectedProperty,
                      selectionIndex: idx,
                      isNestedProperty: true,
                      isRevenueProperty: false,
                    })
                  }
                  label={null}
                  selectorIcon={getNestedPropertyIcon(props.countMenuState.selectedProperty?.listItemGroup)}
                  otherAggregationOptions={[]}
                  eventNames={null}
                  allDataGroups={false}
                  includeCustomProperties={IncludeCustomPropertyType.None}
                  listProperty={propertyItemToSegmentation(
                    props.countMenuState.selectedProperty,
                    props.countMenuState.selectedProperty?.resourceType,
                  )}
                  menuItemTypes={null}
                  // shared
                  key={key}
                  countMenuProps={props.countMenuProps}
                  contextPanelParent={props.contextPanelParent}
                />
              )
            );
          case SubSelectionTypes.AggregationPerUser:
            return (
              <AggregationSelection
                key={key}
                countMenuProps={props.countMenuProps}
                countMenuState={props.countMenuState}
                disableTooltips={props.disableTooltips}
                countTypeSelection={countTypeSelection}
                selectionType={SubSelectionTypes.AggregationPerUser}
                selectionIndex={idx}
                operator={selectedAggregationOperatorPerUser}
                sections={getAggregationsSectionForCountType({
                  countTypeSelections,
                  countTypeIndex: props.countTypeIndex,
                  property: props.countMenuState.selectedProperty,
                  aggregationOperator: selectedAggregationOperator,
                  subSelectionType: SubSelectionTypes.AggregationPerUser,
                  dataGroupId: props.countMenuProps.dataGroupId,
                  dataGroups: props.countMenuProps.dataGroups,
                  queryOrigin: props.countMenuProps.queryOrigin,
                })}
                isOpen={props.countMenuState.isAggregationPerUserMenuOpen}
                onMenuChange={(ev) => props.onMenuChange({isAggregationPerUserMenuOpen: ev.detail.isOpen})}
                onSelectAggregation={props.onSelectAggregation}
              />
            );
          case SubSelectionTypes.Aggregation:
            return (
              <AggregationSelection
                key={key}
                countMenuProps={props.countMenuProps}
                countMenuState={props.countMenuState}
                disableTooltips={props.disableTooltips}
                countTypeSelection={countTypeSelection}
                selectionType={SubSelectionTypes.Aggregation}
                selectionIndex={idx}
                operator={selectedAggregationOperator}
                sections={getAggregationsSectionForCountType({
                  countTypeSelections,
                  countTypeIndex: props.countTypeIndex,
                  property: props.countMenuState.selectedProperty,
                  subSelectionType: SubSelectionTypes.Aggregation,
                  metricType: props.countMenuProps.metricType,
                  dataGroupId: props.countMenuProps.dataGroupId,
                  dataGroups: props.countMenuProps.dataGroups,
                  queryOrigin: props.countMenuProps.queryOrigin,
                })}
                isOpen={props.countMenuState.isAggregationMenuOpen}
                onMenuChange={(ev) => props.onMenuChange({isAggregationMenuOpen: ev.detail.isOpen})}
                onSelectAggregation={props.onSelectAggregation}
              />
            );
          case SubSelectionTypes.CountUsersOnce:
            return (
              <CountUsersOnceSelection
                key={key}
                countMenuProps={props.countMenuProps}
                countMenuState={props.countMenuState}
                timePeriodLabel={props.timePeriodLabel}
                disableTooltips={props.disableTooltips}
                countTypeSelection={countTypeSelection}
                selectionType={SubSelectionTypes.CountUsersOnce}
                selectionIndex={idx}
                isOpen={props.countMenuState.isCountUsersOnceMenuOpen}
                onMenuChange={(ev) => props.onMenuChange({isCountUsersOnceMenuOpen: ev.detail.isOpen})}
                onSelectAggregation={props.onSelectAggregation}
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
};
